<div class="max-w-4xl mx-auto p-6">
    <!-- Header -->
    <div class="mb-6">
        <h1 class="text-2xl font-semibold text-gray-900">Create New Parking Zone</h1>
        <p class="text-sm text-gray-600 mt-1">Add a new parking zone to organize your parking space</p>
    </div>

    <!-- Alert Messages -->
    @if (errorMessage) {
    <jjames-alert type="error" [dismissible]="true" (dismissed)="onClearError()" class="mb-4">
        {{ errorMessage }}
    </jjames-alert>
    }

    @if (successMessage) {
    <jjames-alert type="success" [dismissible]="true" (dismissed)="onClearSuccess()" class="mb-4">
        {{ successMessage }}
    </jjames-alert>
    }

    <!-- Main Form Card -->
    <jjames-card>
        <form [formGroup]="createParkingZoneForm" (ngSubmit)="onSubmit()" class="space-y-6">
            <!-- Basic Information Section -->
            <div class="border-b border-gray-200 pb-6">
                <h3 class="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <!-- Name -->
                    <div class="md:col-span-2">
                        <jjames-input label="Zone Name" placeholder="Enter parking zone name" formControlName="name"
                            [required]="true" [variant]="isFieldInvalid('name') ? 'error' : 'default'">
                        </jjames-input>
                        @if (isFieldInvalid('name')) {
                        <p class="mt-1 text-sm text-red-600">{{ getFieldError('name') }}</p>
                        }
                    </div>

                    <!-- Description -->
                    <div class="md:col-span-2">
                        <jjames-input label="Description" placeholder="Enter zone description (optional)"
                            formControlName="description"
                            [variant]="isFieldInvalid('description') ? 'error' : 'default'">
                        </jjames-input>
                        @if (isFieldInvalid('description')) {
                        <p class="mt-1 text-sm text-red-600">{{ getFieldError('description') }}</p>
                        }
                    </div>

                    <!-- Governance Type -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Governance Type *</label>
                        <select formControlName="governanceType"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            [class.border-red-500]="isFieldInvalid('governanceType')">
                            @for (type of governanceTypes; track type) {
                            <option [value]="type">{{ type }}</option>
                            }
                        </select>
                        @if (isFieldInvalid('governanceType')) {
                        <p class="mt-1 text-sm text-red-600">{{ getFieldError('governanceType') }}</p>
                        }
                    </div>

                    <!-- Capacity -->
                    <div>
                        <jjames-input label="Capacity" placeholder="Enter zone capacity (optional)" type="number"
                            formControlName="capacity" [variant]="isFieldInvalid('capacity') ? 'error' : 'default'">
                        </jjames-input>
                        @if (isFieldInvalid('capacity')) {
                        <p class="mt-1 text-sm text-red-600">{{ getFieldError('capacity') }}</p>
                        }
                    </div>
                </div>
            </div>

            <!-- Address Section -->
            <div class="border-b border-gray-200 pb-6">
                <h3 class="text-lg font-medium text-gray-900 mb-4">Address Information (Optional)</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <!-- Address Line 1 -->
                    <div class="md:col-span-2">
                        <jjames-input label="Address Line 1" placeholder="Enter street address"
                            formControlName="firstAddressLine"
                            [variant]="isFieldInvalid('firstAddressLine') ? 'error' : 'default'">
                        </jjames-input>
                        @if (isFieldInvalid('firstAddressLine')) {
                        <p class="mt-1 text-sm text-red-600">{{ getFieldError('firstAddressLine') }}</p>
                        }
                    </div>

                    <!-- Address Line 2 -->
                    <div class="md:col-span-2">
                        <jjames-input label="Address Line 2"
                            placeholder="Apartment, suite, unit, building, floor, etc. (optional)"
                            formControlName="secondAddressLine"
                            [variant]="isFieldInvalid('secondAddressLine') ? 'error' : 'default'">
                        </jjames-input>
                        @if (isFieldInvalid('secondAddressLine')) {
                        <p class="mt-1 text-sm text-red-600">{{ getFieldError('secondAddressLine') }}</p>
                        }
                    </div>

                    <!-- City/Locality -->
                    <div>
                        <jjames-input label="City/Locality" placeholder="Enter city" formControlName="locality"
                            [variant]="isFieldInvalid('locality') ? 'error' : 'default'">
                        </jjames-input>
                        @if (isFieldInvalid('locality')) {
                        <p class="mt-1 text-sm text-red-600">{{ getFieldError('locality') }}</p>
                        }
                    </div>

                    <!-- Postal Code -->
                    <div>
                        <jjames-input label="Postal Code" placeholder="Enter postal code" formControlName="postCode"
                            [variant]="isFieldInvalid('postCode') ? 'error' : 'default'">
                        </jjames-input>
                        @if (isFieldInvalid('postCode')) {
                        <p class="mt-1 text-sm text-red-600">{{ getFieldError('postCode') }}</p>
                        }
                    </div>

                    <!-- Region/State -->
                    <div>
                        <jjames-input label="Region/State" placeholder="Enter region or state" formControlName="region"
                            [variant]="isFieldInvalid('region') ? 'error' : 'default'">
                        </jjames-input>
                        @if (isFieldInvalid('region')) {
                        <p class="mt-1 text-sm text-red-600">{{ getFieldError('region') }}</p>
                        }
                    </div>

                    <!-- Country Code -->
                    <div>
                        <jjames-input label="Country Code" placeholder="e.g., US, CA, GB" formControlName="countryCode"
                            [variant]="isFieldInvalid('countryCode') ? 'error' : 'default'">
                        </jjames-input>
                        @if (isFieldInvalid('countryCode')) {
                        <p class="mt-1 text-sm text-red-600">{{ getFieldError('countryCode') }}</p>
                        }
                    </div>

                    <!-- Latitude -->
                    <div>
                        <jjames-input label="Latitude" placeholder="Enter latitude (-90 to 90)" type="number" step="any"
                            formControlName="latitude" [variant]="isFieldInvalid('latitude') ? 'error' : 'default'">
                        </jjames-input>
                        @if (isFieldInvalid('latitude')) {
                        <p class="mt-1 text-sm text-red-600">{{ getFieldError('latitude') }}</p>
                        }
                    </div>

                    <!-- Longitude -->
                    <div>
                        <jjames-input label="Longitude" placeholder="Enter longitude (-180 to 180)" type="number"
                            step="any" formControlName="longitude"
                            [variant]="isFieldInvalid('longitude') ? 'error' : 'default'">
                        </jjames-input>
                        @if (isFieldInvalid('longitude')) {
                        <p class="mt-1 text-sm text-red-600">{{ getFieldError('longitude') }}</p>
                        }
                    </div>
                </div>
            </div>

            <!-- Additional Settings Section -->
            <div class="pb-6">
                <h3 class="text-lg font-medium text-gray-900 mb-4">Additional Settings (Optional)</h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <!-- External Organization ID -->
                    <div>
                        <jjames-input label="External Organization ID" placeholder="Enter external organization ID"
                            formControlName="externalOrganizationId">
                        </jjames-input>
                    </div>



                    <!-- Zone Infrastructure ID -->
                    <div class="md:col-span-2">
                        <jjames-input label="Zone Infrastructure ID" placeholder="Enter zone infrastructure ID"
                            formControlName="zoneInfrastructureId">
                        </jjames-input>
                    </div>
                </div>
            </div>

            <!-- Form Actions -->
            <div class="flex flex-col sm:flex-row justify-end gap-3 pt-6 border-t border-gray-200">
                <jjames-button variant="secondary" type="button" (clicked)="onCancel()" [disabled]="isSubmitting">
                    Cancel
                </jjames-button>

                <jjames-button variant="primary" type="submit"
                    [disabled]="isSubmitting || createParkingZoneForm.invalid" [loading]="isSubmitting"
                    iconLeft="fas fa-plus">
                    {{ isSubmitting ? 'Creating...' : 'Create Parking Zone' }}
                </jjames-button>
            </div>
        </form>
    </jjames-card>
</div>